import React, { useState } from 'react';
import { getVersionDisplay } from '../utils/version';
import { toProperCase } from '../utils/textUtils';
import { API_ENDPOINTS } from '../constants/api';
import './LandingPage.css';

interface LandingPageProps {
  onLogin: (email: string, password: string) => void;
  onSignup: (signupData: SignupData) => Promise<{ success: boolean; message: string; email: string } | void>;
  isLoading?: boolean;
  error?: string;
}

interface SignupData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  organization: string;
  mobile?: string;
  referred_by?: string;
  engagement_level?: number;
}

const LandingPage: React.FC<LandingPageProps> = ({ 
  onLogin, 
  onSignup, 
  isLoading = false, 
  error 
}) => {
  // Check if user has previously created an account
  const hasCreatedAccount = localStorage.getItem('hasCreatedAccount') === 'true';
  
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasViewedTerms, setHasViewedTerms] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization: '',
    referred_by: '',
    referred_by_other: '',
    mobile: '',
    engagement_level: 1
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSignupMode) {
      if (formData.password !== formData.confirmPassword) {
        return; // Handle password mismatch
      }
      
      // Set flag that user has created an account
      localStorage.setItem('hasCreatedAccount', 'true');
      
      const signupData: SignupData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        organization: formData.organization,
        mobile: formData.mobile || undefined,
        referred_by: formData.referred_by === 'Other' ? formData.referred_by_other : formData.referred_by || undefined,
        engagement_level: formData.engagement_level
      };
      
      try {
        const result = await onSignup(signupData);
        if (result && result.success) {
          // Show verification message
          setVerificationEmail(signupData.email);
          setShowVerificationMessage(true);
          // Reset form
          setFormData({
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirmPassword: '',
            organization: '',
            referred_by: '',
            referred_by_other: '',
            mobile: '',
            engagement_level: 1
          });
        }
      } catch (error) {
        // Error handling/message display is handled in parent component
      }
    } else {
      onLogin(formData.email, formData.password);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Apply proper case conversion to specific fields
    const fieldsToProperCase = ['first_name', 'last_name', 'organization'];
    const processedValue = fieldsToProperCase.includes(name) ? toProperCase(value) : value;
    
    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });

      if (response.ok) {
        setResetEmailSent(true);
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setResetEmailSent(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail }),
      });

      if (response.ok) {
        alert('Verification email sent! Please check your inbox.');
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to resend verification email. Please try again.');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      alert('Failed to resend verification email. Please try again.');
    }
  };

  const handleViewTerms = () => {
    // Mark terms as viewed immediately since we're showing static content
    setHasViewedTerms(true);
  };

  const handleAcceptTerms = () => {
    if (!hasViewedTerms) {
      return; // Don't allow acceptance without viewing
    }
    
    setHasAcceptedTerms(true);
    setShowThankYouMessage(true);
    
    // Close modal after a brief delay to show thank you message
    setTimeout(() => {
      setShowTermsModal(false);
      setShowThankYouMessage(false);
      setHasViewedTerms(false);
      setIsSignupMode(true); // Show the signup form
    }, 2000);
  };

  const handleSwitchToSignup = () => {
    // First show Knowledge Base modal
    setShowKnowledgeBaseModal(true);
  };

  const handleKnowledgeBaseReady = () => {
    // Close Knowledge Base modal and show Terms modal
    setShowKnowledgeBaseModal(false);
    setShowTermsModal(true);
    // Mark terms as viewed immediately since we're showing static content
    setHasViewedTerms(true);
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <div className="landing-header">
          <h1 className="landing-title">Welcome to RAVAH | CARBON </h1>
          <p className="landing-subtitle">Advanced crop carbon tracking platform</p>
          <div><span className="version-badge">{getVersionDisplay()}</span></div>
        </div>

        <div className="auth-container">
          <div className="auth-tabs">
            <button
              className={`auth-tab ${!isSignupMode ? 'active' : ''}`}
              onClick={() => {
                setIsSignupMode(false);
                setHasAcceptedTerms(false); // Reset terms acceptance when switching to login
              }}
            >
              Log In
            </button>
            <button
              className={`auth-tab ${isSignupMode ? 'active' : ''}`}
              onClick={handleSwitchToSignup}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="auth-form" autoComplete="off">
            <div className="form-content">
              {isSignupMode && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="first_name">First Name *</label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required={isSignupMode}
                        placeholder="Enter your first name"
                        autoComplete="off"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="last_name">Last Name *</label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required={isSignupMode}
                        placeholder="Enter your last name"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="organization">Organization Name *</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      required={isSignupMode}
                      placeholder="Enter organization name"
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="mobile">Mobile Phone</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder="Enter mobile phone number"
                    />
                  </div>

                 {/*} <div className="form-group">
                    <label htmlFor="engagement_level">Engagement Level *</label>
                    <select
                      id="engagement_level"
                      name="engagement_level"
                      value={formData.engagement_level}
                      onChange={handleInputChange}
                      required
                    >
                      <option value={1}>Level 1 - Use GREET defaults for all data</option>
                      <option value={2}>Level 2 - Your farm data plus GREET defaults for some items</option>
                    </select>
                  </div>*/}

                  <div className="form-group">
                    <label htmlFor="referred_by">Referred By</label>
                    <select
                      id="referred_by"
                      name="referred_by"
                      value={formData.referred_by}
                      onChange={handleInputChange}
                    >
                      <option value="">Select referral source (optional)</option>
                      <option value="RAVAH Web Site">RAVAH|TECH</option>
                      <option value="Agrellus">Agrellus</option>
                      <option value="Millpont">Millpont</option>
                      <option value="Agricap">Agricap</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {formData.referred_by === 'Other' && (
                    <div className="form-group">
                      <label htmlFor="referred_by_other">Please specify</label>
                      <input
                        type="text"
                        id="referred_by_other"
                        name="referred_by_other"
                        value={formData.referred_by_other}
                        onChange={handleInputChange}
                        placeholder="Enter referral source"
                        required
                      />
                    </div>
                  )}
                </>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label htmlFor="password">Password</label>
                  {!isSignupMode && (
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="forgot-password-link"
                      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.875rem', padding: 0, textDecoration: 'underline' }}
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                />
              </div>

              {isSignupMode && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required={isSignupMode}
                    placeholder="Confirm your password"
                  />
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <span className="error-text">Passwords do not match</span>
                  )}
                </div>
              )}

              {error && <div className="error-message">{error}</div>}
            </div>

            <div className="form-button-container">
              <button
                type="submit"
                className="auth-button"
                disabled={isLoading || (isSignupMode && formData.password !== formData.confirmPassword)}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  isSignupMode ? 'Create Account' : 'Log In'
                )}
              </button>
            </div>
          </form>

          <div className="auth-footer">
            <p>
              {isSignupMode ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                className="link-button"
                onClick={isSignupMode ? () => {
                  setIsSignupMode(false);
                  setHasAcceptedTerms(false); // Reset terms acceptance when switching to login
                } : handleSwitchToSignup}
              >
                {isSignupMode ? 'Log In' : 'Sign Up'}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Knowledge Base Modal */}
      {showKnowledgeBaseModal && (
        <div className="terms-modal-overlay">
          <div className="terms-modal knowledge-base-modal">
            <div className="terms-modal-header">
              <h2>Before You Create Your Account</h2>
              <button 
                className="terms-modal-close"
                onClick={() => setShowKnowledgeBaseModal(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <div className="terms-modal-content">
              <div className="terms-notice">
                <h3>👋 Welcome to RAVAH | CARBON</h3>
                <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: '20px' }}>
                  Quick summary to help you get started with our platform.
                </p>
                <h4 style={{ marginTop: '16px', marginBottom: '8px' }}>📚 Getting Started Guide</h4>
                <p>
                  We recommend you check out our Getting Started Knowledge Base articles to help you understand the onboarding process.
                </p>
                <p>
                  The Knowledge Base will walk you through setting up your account, adding fields, and understanding how to use the platform effectively.
                </p>
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <a 
                    href="https://ravahcarbon.tawk.help/category/setting-up" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="knowledge-base-button"
                    style={{ display: 'inline-block', padding: '12px 24px', fontSize: '16px' }}
                  >
                    📚 Visit Knowledge Base
                  </a>
                </div>
                <p style={{ marginTop: '30px', color: '#666' }}>
                  Once you're ready to create your account, click the button below to proceed.
                </p>
              </div>
              
              <div className="terms-modal-footer">
                <button 
                  className="btn-accept"
                  onClick={handleKnowledgeBaseReady}
                >
                  OK, I'm Ready to Create an Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => hasViewedTerms && setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Terms & Conditions</h2>
              <button 
                className="terms-modal-close"
                onClick={() => setShowTermsModal(false)}
                disabled={!hasViewedTerms}
                title={!hasViewedTerms ? "You must view the terms before closing" : "Close"}
              >
                ×
              </button>
            </div>
            
            <div className="terms-modal-content">
              {showThankYouMessage ? (
                <div className="thank-you-message">
                  <div className="thank-you-icon">✅</div>
                  <h3>Thank You!</h3>
                  <p>Now please proceed with creating your account.</p>
                </div>
              ) : (
                <>
                  <div className="terms-notice">
                    <p>
                      <strong>Important:</strong> By creating an account and using the RAVAH|CARBON software, 
                      you are agreeing to the terms and conditions of the RAVAH|CARBON contract.
                    </p>
                    <p>
                      Please review the complete terms and conditions before proceeding with your account creation.
                    </p>
                  </div>
                  
                  <div className="terms-iframe-container">
                    <div className="terms-content">
                      <div className="terms-summary-header">
                        <h3>Quick Summary of our Terms & Conditions</h3>
                      </div>
                      
                      <div className="terms-summary-body">
                        <details>
                          <summary>🌱 1. It's Free to Join</summary>
                          <ul>
                            <li>No registration or setup fees. Register your fields at no cost</li>
                            <li>Free Carbon Intensity scoring for Corn, Soybean & Sorghum (more crop types coming soon)</li>
                            <li>RAVAH and our partners cover all the costs to create your Carbon Intensity Certificates</li>
                            <li>Register any of your fields, not just those used for biofuel feedstock</li>
                            <li>You'll have free access to certified Protocol Managers to guide you</li>
                            <li>No extra software investment needed. Our farm management tools are all included</li>
                          </ul>
                        </details>

                        <details>
                          <summary>🔒 2. Your Data Stays Private</summary>
                          <ul>
                            <li>We designed our RAVAH | CARBON platforms with anonymity and data security at the core</li>
                            <li>RAVAH buys your data on consignment to build and sell Carbon Intensity Certificates</li>
                            <li>Certificates are issued under RAVAH's name — your identity and your farm's identity stays completely private</li>
                          </ul>
                        </details>

                        <details>
                          <summary>🧾 3. Trusted Certificates</summary>
                          <ul>
                            <li>Our Monitoring, Reporting & Verification system runs on five levels of data integrity - the higher the level, the greater the integrity and higher the certificate value</li>
                            <li>Our Carbon Intensity Certificates are "investment-grade" and highly trusted</li>
                            <li>All your field polygons are registered in RAVAH's name on the  METI<sup style={{ fontSize: '0.6em' }}>TM</sup> system, a secure digital clearinghouse</li>
                          </ul>
                        </details>

                        <details>
                          <summary>💼 4. RAVAH: Your Market Partner - You Earn 80%</summary>
                          <ul>
                            <li>RAVAH promotes millions of acres of verified data to national buyers</li>
                            <li>Buyers trust our ISO 9001 validated process for auditability, transparency and traceability</li>
                            <li><b>You keep 80% of the net sale value</b></li>
                          </ul>
                        </details>

                        <details>
                          <summary>🌾 5. Plan Ahead (Coming Soon)</summary>
                          <ul>
                            <li>Predict Carbon Intensity Scores before planting</li>
                            <li>Plan ahead and even pre-sell your Carbon Intensity Certificates early in the season</li>
                            <li>RAVAH will offer insurance against crop failure to help protect your expected income</li>
                          </ul>
                        </details>

                        <details>
                          <summary>📋 6. Your Part</summary>
                          <ul>
                            <li>Grow your crops on the registered fields and aim for Carbon Intensity scores below GREET defaults</li>
                            <li>Keep your supporting records handy — invoices for inputs, harvest data, fuel use, etc. — for audits and verification</li>
                          </ul>
                        </details>

                        <div className="terms-summary-footer">
                          <p>You're joining a secure, farmer-first platform built to unlock new value from your data 🌾</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="terms-modal-footer">
              <div className="terms-footer-buttons">
                <a 
                  href="/RAVAH-CARBON-Terms-Conditions-v1.1.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="terms-view-full-btn"
                >
                  📄 View Full Terms & Conditions
                </a>
                <button
                  className="terms-accept-btn"
                  onClick={handleAcceptTerms}
                  disabled={!hasViewedTerms}
                >
                  {hasViewedTerms ? 'I Accept Terms & Conditions' : 'Please View Terms First'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="terms-modal-overlay" onClick={() => !resetEmailSent && setShowForgotPassword(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Reset Password</h2>
              <button 
                className="terms-modal-close"
                onClick={() => setShowForgotPassword(false)}
                disabled={resetEmailSent}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <div className="password-reset-modal-content">
              {resetEmailSent ? (
                <div className="thank-you-message">
                  <div className="thank-you-icon">✓</div>
                  <h3>Email Sent!</h3>
                  <p>If an account exists with this email, you'll receive a password reset link shortly.</p>
                  <p>Please check your email and follow the instructions to reset your password.</p>
                </div>
              ) : (
                <>
                  <p>Enter your email address and we'll send you a link to reset your password.</p>
                  <form onSubmit={handleForgotPassword} className="forgot-password-form">
                    <div className="form-group" style={{ marginTop: '20px' }}>
                      <label htmlFor="resetEmail">Email Address</label>
                      <input
                        type="email"
                        id="resetEmail"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        placeholder="Enter your email"
                        autoFocus
                      />
                    </div>
                    <div className="form-actions" style={{ marginTop: '20px' }}>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(false)}
                        className="btn-secondary"
                        style={{ marginRight: '10px' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Send Reset Link
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Message Modal */}
      {showVerificationMessage && (
        <div className="terms-modal-overlay" onClick={() => setShowVerificationMessage(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Verify Your Email</h2>
              <button 
                className="terms-modal-close"
                onClick={() => setShowVerificationMessage(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            
            <div className="password-reset-modal-content">
              <div className="thank-you-message">
                <div className="thank-you-icon">📧</div>
                <h3>Check Your Email</h3>
                <p>We've sent a verification email to <strong>{verificationEmail}</strong></p>
                <p>Please check your <strong>inbox</strong> and click the verification link to complete your registration.</p>
                <p style={{ fontSize: '0.9rem', color: '#d32f2f', marginTop: '15px', padding: '10px', background: '#ffebee', borderRadius: '4px', border: '1px solid #ffcdd2' }}>
                  <strong>⚠️ Can't find the email?</strong> Please check your <strong>spam/junk folder</strong> - verification emails sometimes get filtered there.
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '15px' }}>
                  <strong>Note:</strong> The verification link will expire in 24 hours.
                </p>
                <div style={{ marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
                    Didn't receive the email?
                  </p>
                  <button
                    onClick={handleResendVerification}
                    className="btn-secondary"
                    style={{ width: '100%' }}
                  >
                    Resend Verification Email
                  </button>
                </div>
                <div style={{ marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      setShowVerificationMessage(false);
                      setIsSignupMode(false);
                    }}
                    className="btn-primary"
                    style={{ width: '100%' }}
                  >
                    Return to Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage; 